import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FAQ, { getFAQSchema } from '@/pages/home/components/FAQ';
import ServiceGrid from './components/ServiceGrid';
import ProcessFlow from './components/ProcessFlow';
import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/hizmetler/performans-pazarlama';
const pageUrl = `${siteUrl}${pagePath}`;

const perfFAQItems = [
  {
    question: 'Performans pazarlama nedir?',
    answer:
      'Performans pazarlama; dijital pazarlama ve reklam yatırımlarının ölçülebilir ticari sonuçlar üzerinden yönetilmesidir.',
  },
  {
    question: 'Google Ads ve Meta Ads yönetiyor musunuz?',
    answer:
      'Evet. Google Ads ve Meta Ads yönetiminin yanı sıra kampanya stratejisi, kreatif testleri, landing page, CRO ve analitik süreçlerini de birlikte ele alıyoruz.',
  },
  {
    question: 'Ne kadar reklam bütçesiyle başlanmalı?',
    answer:
      'Her marka için doğru bütçe farklıdır. Sektör, hedef, satış döngüsü, müşteri edinme maliyeti ve mevcut dönüşüm verilerine göre belirlenir.',
  },
  {
    question: 'Reklam veriyoruz ama satış alamıyoruz. Sorun ne olabilir?',
    answer:
      'Sorun reklam olabilir. Ama her zaman reklam değildir. Hedef kitle, teklif, mesaj, kreatif, web sitesi, kullanıcı deneyimi veya satış sürecindeki herhangi bir kırılma performansı etkileyebilir.',
  },
  {
    question: 'Performans pazarlama sadece e-ticaret için mi?',
    answer:
      'Hayır. E-ticaretin yanında B2B, SaaS, teknoloji ve endüstriyel şirketlerde lead generation ve müşteri edinme süreçleri için de uygulanabilir.',
  },
  {
    question: 'Reklam bütçesini artırmak her zaman daha fazla satış getirir mi?',
    answer:
      'Hayır. Ölçeklenebilir bir sistem kurulmadan bütçeyi artırmak, verimsizliği büyütebilir.',
  },
];

const faqSchema = getFAQSchema(perfFAQItems);

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'Performans Pazarlama Hizmeti',
      provider: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
      description:
        'Google Ads, Meta Ads, CRO ve analitikle reklam bütçenizi kontrollü büyümeye dönüştürün. BIFF Digital performans pazarlamayı strateji ve kreatifle birlikte yönetir.',
      url: pageUrl,
      areaServed: { '@type': 'Country', name: 'Turkey' },
      serviceType: 'Performans Pazarlama',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Performans Pazarlama Hizmet Detayları',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Google Ads Yönetimi', description: 'Arama ağı, Performance Max, YouTube reklam yönetimi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Meta Ads Yönetimi', description: 'Instagram ve Facebook reklam yönetimi, kreatif test' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'CRO - Dönüşüm Oranı Optimizasyonu', description: 'Landing page ve kullanıcı deneyimi analizi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Funnel & Analytics', description: 'Tam huni analizi ve veri odaklı optimizasyon' } },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Hizmetler', item: `${siteUrl}/hizmetler` },
        { '@type': 'ListItem', position: 3, name: 'Performans Pazarlama', item: pageUrl },
      ],
    },
    {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-summary'],
    },
    faqSchema,
  ],
};

export default function PerformansPazarlama() {
  useEffect(() => {
    document.title = 'Performans Pazarlama Ajansı | Google & Meta Ads | BIFF Digital';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* ==================== HERO ==================== */}
        <section className="section-padding pb-0 md:pb-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-4">
              HİZMETLER
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-50 tracking-tighter leading-none mb-8">
              Performans<br />Pazarlama
            </h1>
            <div className="h-px w-full bg-gradient-to-r from-white/15 via-white/5 to-transparent mb-10" />
            <p className="text-xl md:text-2xl text-foreground-200 font-medium leading-relaxed mb-6">
              Reklam vermek başka.<br />
              <span className="text-primary-500">Büyümeyi yönetmek başka.</span>
            </p>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-2xl speakable-summary">
              Bir reklam kampanyasının başarılı olması, reklam yöneticisindeki grafiğin yukarı çıkmasından ibaret değildir. Doğru insanı bulmanız gerekir. Doğru mesajı vermeniz gerekir. Ve o insanı doğru aksiyona götürmeniz gerekir.
            </p>
          </div>
        </section>

        {/* ==================== SECTION 1: Approach ==================== */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-sm text-foreground-400 leading-relaxed mb-8">
              BIFF Digital&apos;de performans pazarlamayı yalnızca medya satın alma olarak ele almıyoruz. Markanın konumlandırmasından kreatife, hedeflemeden landing page&apos;e, kullanıcı davranışından dönüşüm verisine kadar bütün sistemi birlikte okuyoruz.
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500">
              <p className="text-base md:text-lg text-foreground-100 font-medium leading-relaxed">
                Çünkü reklam bütçesini artırmak kolaydır.<br />
                <strong className="text-foreground-50">Önemli olan, bütçeyi neden artırdığınızı bilmektir.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* ==================== SECTION 2: Visibility Is Not Growth ==================== */}
        <section className="section-padding bg-background-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              Görünürlük Bir Hedef Değil,<br />Bir Araçtır.
            </h2>

            <div className="space-y-4 mb-10">
              {[
                'Binlerce kişiye ulaşabilirsiniz.',
                'Milyonlarca gösterim alabilirsiniz.',
                'Tıklama maliyetinizi düşürebilirsiniz.',
              ].map((line, i) => (
                <p key={i} className="text-base text-foreground-300 leading-relaxed">
                  {line}
                </p>
              ))}
            </div>

            <div className="glass-card rounded-2xl p-6 md:p-8 border border-white/5">
              <p className="text-sm text-foreground-400 mb-4">
                Ama bunların hiçbiri tek başına büyüme değildir.
              </p>
              <p className="text-lg md:text-xl font-bold text-foreground-50 mb-6">
                Biz performansı şu soruyla değerlendiriyoruz:
              </p>
              <p className="text-xl md:text-2xl font-black text-primary-500 font-heading tracking-tight mb-8">
                &ldquo;Bu kampanya markaya ne kazandırdı?&rdquo;
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {['Satış mı?', 'Nitelikli lead mi?', 'Yeni müşteri mi?', 'Daha düşük müşteri edinme maliyeti mi?', 'Daha yüksek yaşam boyu değer mi?'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-foreground-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-sm text-foreground-400 mt-6 font-medium">
                Hedefi belirlemeden metrik seçmiyoruz.
              </p>
            </div>
          </div>
        </section>

        {/* ==================== SECTION 3: Ne Yapıyoruz ==================== */}
        <section className="section-padding">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">
                NE YAPIYORUZ?
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Dört Alanda<br />Performans Yönetimi
              </h2>
            </div>
            <ServiceGrid />
          </div>
        </section>

        {/* ==================== SECTION 4: Nasıl Çalışıyoruz ==================== */}
        <section className="section-padding bg-background-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">
                NASIL ÇALIŞIYORUZ?
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Audit &rarr; Strateji &rarr; Test<br />&rarr; Optimize &rarr; Scale
              </h2>
            </div>
            <ProcessFlow />
          </div>
        </section>

        {/* ==================== SECTION 5: Neden Farklı ==================== */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              BIFF Performans Pazarlamasını<br />Neden Farklı Yapıyor?
            </h2>

            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500 mb-8">
              <p className="text-base md:text-lg text-foreground-50 font-bold leading-relaxed mb-3">
                Çünkü reklam hesabına bakıp marka hakkında karar vermiyoruz.
              </p>
              <p className="text-base md:text-lg text-foreground-300 leading-relaxed">
                Markaya bakıp reklam hesabını yeniden okuyoruz.
              </p>
            </div>

            <p className="text-sm text-foreground-400 mb-6 font-medium">
              Bir kampanya neden çalışmıyor?
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {[
                'Hedefleme yanlış olabilir.',
                'Kreatif kötü olabilir.',
                'Teklif zayıf olabilir.',
                'Marka güven vermiyor olabilir.',
                'Kullanıcı doğru yere gönderilmiyor olabilir.',
                'Pazarda markanın söyleyecek yeni bir şeyi yoktur.',
              ].map((reason, i) => (
                <div key={i} className="flex items-start gap-3 text-sm text-foreground-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 shrink-0" />
                  {reason}
                </div>
              ))}
            </div>

            <p className="text-sm text-foreground-400 leading-relaxed mb-6">
              Biz yalnızca problemi bulmaya değil, problemin nereden çıktığını anlamaya çalışıyoruz.
            </p>

            <div className="glass-card rounded-2xl p-6 md:p-8">
              <p className="text-xs text-foreground-500 uppercase tracking-widest mb-3">
                Bu nedenle performans ekibimiz
              </p>
              <p className="text-lg md:text-xl font-bold text-foreground-50 leading-relaxed">
                strateji + kreatif + teknoloji + veri + kullanıcı psikolojisi
              </p>
              <p className="text-sm text-foreground-400 mt-3">
                ile birlikte çalışır.
              </p>
            </div>
          </div>
        </section>

        {/* ==================== SECTION 6: Kimler İçin ==================== */}
        <section className="section-padding bg-background-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              Kimler İçin?
            </h2>

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {[
                'E-ticaret markaları',
                'B2B şirketler',
                'Endüstriyel firmalar',
                'Teknoloji şirketleri',
                'SaaS ve yazılım markaları',
                'Ölçeklenmek isteyen girişimler',
                'Yüksek büyüme hedefi olan şirketler',
              ].map((audience, i) => (
                <div key={i} className="glass-card-hover rounded-xl p-4 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-primary-500 text-sm" />
                  </div>
                  <span className="text-sm font-medium text-foreground-200">{audience}</span>
                </div>
              ))}
            </div>

            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500">
              <p className="text-sm text-foreground-300 leading-relaxed">
                Özellikle reklam bütçesi büyüdükçe{' '}
                <span className="text-foreground-400 line-through">&ldquo;Daha fazla harcayalım.&rdquo;</span>
                {' '}yerine{' '}
                <strong className="text-foreground-50">
                  &ldquo;Neden daha fazla harcamalıyız?&rdquo;
                </strong>
                {' '}sorusunu sormaya başlayan markalarla çalışıyoruz.
              </p>
            </div>
          </div>
        </section>

        {/* ==================== SECTION 7: FAQ ==================== */}
        <FAQ
          items={perfFAQItems}
          sectionTitle="SIK SORULANLAR"
          sectionSubtitle="PERFORMANS PAZARLAMA"
          sectionSummary="Performans pazarlama hakkında en sık sorulan soruları, doğrudan ve net cevaplarla burada topladık."
          speakableClass={false}
        />

        {/* ==================== SECTION 8: CTA ==================== */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-bl-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tighter mb-4">
                  Markanızın reklamlarını değil,<br />
                  <span className="text-primary-500">büyüme sistemini</span> konuşalım.
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  Reklam hesabınızın başında daha fazla zaman geçirmek yerine, neden istediğiniz sonucu alamadığınızı birlikte inceleyelim.
                </p>
                <a href="/#contact" className="btn-primary text-base px-8 py-4">
                  Görüşme Planla
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}